import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Badge } from "./ui/badge";
import { BookOpen, MessageCircle, Mail, ExternalLink, Search, Star, Clock, Target, BarChart3 } from "lucide-react";

export function Help() {
  const faqs = [
    {
      question: "How do I create my first decision?",
      answer: "Click the 'New Decision' button in the sidebar or header. Fill out the decision title, description, rationale, and set your initial confidence rating. You can also add tags and emotions to categorize your decision."
    },
    {
      question: "What are review intervals?",
      answer: "Review intervals are scheduled times (in days) when you'll be reminded to reflect on your decision. Common intervals are 7, 30, and 90 days. You can customize these when creating a decision."
    },
    {
      question: "How does the rating system work?",
      answer: "The Day 0 rating is your initial confidence in the decision on a scale of 1-10. During reviews, you can update this rating based on how the decision turned out."
    },
    {
      question: "Can I edit decisions after creating them?",
      answer: "Yes! Go to 'My Decisions', find your decision, and click the edit button. You can update all fields except the creation date."
    },
    {
      question: "How do I complete a review?",
      answer: "When a review is due, you'll see it in the 'Reviews' section. Click 'Complete Review' and provide your updated thoughts, current rating, and lessons learned."
    },
    {
      question: "What insights can I get from Analytics?",
      answer: "The Analytics page shows trends in your decision-making, confidence ratings over time, most-used tags, and personalized recommendations to improve your decision quality."
    }
  ];

  const guides = [
    {
      title: "Getting Started with Decisionr.xyz",
      description: "Learn the basics of recording and reviewing decisions",
      icon: BookOpen,
      steps: ["Create your account", "Record your first decision", "Set up review intervals", "Complete your first review"]
    },
    {
      title: "Effective Decision Tracking",
      description: "Best practices for meaningful decision journaling",
      icon: Target,
      steps: ["Write clear rationales", "Use consistent tags", "Be honest about emotions", "Follow through on reviews"]
    },
    {
      title: "Using Analytics",
      description: "Make sense of your decision-making patterns",
      icon: BarChart3,
      steps: ["Review your trends", "Identify patterns", "Act on recommendations", "Track improvements"]
    }
  ];

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1>Help &amp; Support</h1>
          <p className="text-muted-foreground">
            Find answers to common questions and get help with Decisionr.xyz
          </p>
        </div>

        <Tabs defaultValue="faq" className="space-y-6">
          <TabsList>
            <TabsTrigger value="faq">FAQ</TabsTrigger>
            <TabsTrigger value="guides">Guides</TabsTrigger>
            <TabsTrigger value="contact">Contact</TabsTrigger>
          </TabsList>

          <TabsContent value="faq" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Frequently Asked Questions</CardTitle>
                <CardDescription>Quick answers to common questions</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                    <Input placeholder="Search FAQ..." className="pl-10" />
                  </div>
                </div>
                
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger>{faq.question}</AccordionTrigger>
                      <AccordionContent>{faq.answer}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="guides" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {guides.map((guide) => (
                <Card key={guide.title} className="hover:shadow-md transition-shadow">
                  <CardHeader>
                    <div className="flex items-center gap-3">
                      <guide.icon className="h-8 w-8 text-primary" />
                      <div>
                        <CardTitle className="text-lg">{guide.title}</CardTitle>
                        <CardDescription>{guide.description}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {guide.steps.map((step, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          <Badge variant="outline" className="w-6 h-6 rounded-full p-0 flex items-center justify-center">
                            {index + 1}
                          </Badge>
                          <span>{step}</span>
                        </div>
                      ))}
                    </div>
                    <Button variant="outline" className="w-full mt-4">
                      <ExternalLink className="h-4 w-4 mr-2" />
                      View Guide
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Quick Tips</CardTitle>
                <CardDescription>Make the most of Decisionr.xyz</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                    <Star className="h-5 w-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-800">Be Specific</p>
                      <p className="text-sm text-blue-700">
                        Write detailed rationales to better understand your thinking later.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                    <Clock className="h-5 w-5 text-green-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-green-800">Set Reminders</p>
                      <p className="text-sm text-green-700">
                        Enable notifications to stay on top of your review schedule.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                    <Target className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-purple-800">Track Patterns</p>
                      <p className="text-sm text-purple-700">
                        Use consistent tags to identify patterns in your decision-making.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-orange-50 rounded-lg">
                    <BarChart3 className="h-5 w-5 text-orange-600 mt-0.5" />
                    <div>
                      <p className="font-medium text-orange-800">Review Analytics</p>
                      <p className="text-sm text-orange-700">
                        Check your analytics regularly to improve decision quality.
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="contact" className="space-y-6">
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader>
                  <CardTitle>Get in Touch</CardTitle>
                  <CardDescription>We're here to help you succeed</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <Mail className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Email Support</p>
                      <p className="text-sm text-muted-foreground">support@decisionr.xyz</p>
                      <p className="text-xs text-muted-foreground">Response within 24 hours</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <MessageCircle className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Live Chat</p>
                      <p className="text-sm text-muted-foreground">Available Mon-Fri, 9AM-5PM PST</p>
                      <Button variant="outline" size="sm" className="mt-2">
                        Start Chat
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 border rounded-lg">
                    <BookOpen className="h-5 w-5 text-primary" />
                    <div>
                      <p className="font-medium">Documentation</p>
                      <p className="text-sm text-muted-foreground">Comprehensive guides and tutorials</p>
                      <Button variant="outline" size="sm" className="mt-2">
                        <ExternalLink className="h-4 w-4 mr-1" />
                        View Docs
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Send us a Message</CardTitle>
                  <CardDescription>Describe your issue or question</CardDescription>
                </CardHeader>
                <CardContent>
                  <form className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="subject">Subject</Label>
                      <Input id="subject" placeholder="What can we help you with?" />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        placeholder="Describe your question or issue in detail..."
                        rows={6}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="priority">Priority</Label>
                      <select className="w-full p-2 border rounded-md">
                        <option>Low - General question</option>
                        <option>Medium - Need help</option>
                        <option>High - Blocking issue</option>
                      </select>
                    </div>

                    <Button className="w-full">
                      <Mail className="h-4 w-4 mr-2" />
                      Send Message
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>System Status</CardTitle>
                <CardDescription>Current status of Decisionr.xyz services</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">API Services</span>
                    <Badge className="bg-green-600">Operational</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Database</span>
                    <Badge className="bg-green-600">Operational</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Email Notifications</span>
                    <Badge className="bg-green-600">Operational</Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Analytics Processing</span>
                    <Badge className="bg-green-600">Operational</Badge>
                  </div>
                </div>
                <Button variant="outline" className="w-full mt-4">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Status Page
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
    </div>
  );
}